// src/pages/payment/PaymentProvidersPage.tsx
// Admin: payment providers — connect, configure, and test a payment service
// provider (Stripe, Adyen, ...). Rare, setup-time task; list + detail rail.
//
// Covers: ListPaymentProviders, CreatePaymentProvider, GetPaymentProvider,
// UpdatePaymentProvider, DeletePaymentProvider, TestPaymentProvider.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { usePaymentPaymentProvider, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { SplitView } from '@/components/SplitView';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { PaymentProvider } from '@core/types/payment';
import { formatDate, humanizeEnum } from '@/features/payment/format';
import { mapProviderStatusToVariant } from '@/features/payment/mapStatusToVariant';
import { ProviderFormModal, type ProviderFormValue } from '@/features/payment/ProviderFormModal';

export const PaymentProvidersPage = observer(() => {
  const store = usePaymentPaymentProvider();
  const { t } = useI18n();
  const [selected, setSelected] = useState<PaymentProvider | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentProvider | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PaymentProvider | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  useEffect(() => {
    store.ListPaymentProviders({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const submit = async (value: ProviderFormValue) => {
    const payload = {
      entity: editing ? (editing.entity as any) : ({} as any),
      name: value.name,
      display_name: value.display_name,
      description: value.description,
      type: value.type as any,
      provider_code: value.provider_code,
      credential_id: value.credential_id,
      supported_currencies: value.supported_currencies.split(',').map((s) => s.trim()).filter(Boolean),
      supported_countries: value.supported_countries.split(',').map((s) => s.trim()).filter(Boolean),
      supported_methods: [],
      metadata: {},
    };
    if (editing?.entity?.id) {
      const updated = await store.UpdatePaymentProvider(editing.entity.id, payload as any);
      setSelected(updated);
    } else {
      await store.CreatePaymentProvider(payload as any);
    }
    setFormOpen(false);
    setEditing(null);
  };

  const handleTest = async (p: PaymentProvider) => {
    if (!p.entity?.id) return;
    setTestingId(p.entity.id);
    try {
      const res = await store.TestPaymentProvider(p.entity.id, {} as any);
      setTestResult({ id: p.entity.id, success: res.success, message: res.success ? t('payment.providers.testSuccess') : (res.error_message || t('payment.providers.testFailed')) });
    } catch {
      setTestResult({ id: p.entity.id, success: false, message: t('payment.providers.testFailed') });
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeletePaymentProvider(deleteTarget.entity.id);
    if (selected?.entity?.id === deleteTarget.entity.id) setSelected(null);
    setDeleteTarget(null);
  };

  const list = (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('payment.providers.title')}</span>
          </nav>
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('payment.providers.title')}</h1>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>{t('payment.providers.create')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('payment.providers.name')}</TableHead>
              <TableHead>{t('payment.providers.type')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('payment.providers.successRate')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={5}>
                <span className="table-empty-message">{t('payment.providers.empty')}</span>
                <p className="mt-1 text-sm text-muted-foreground">{t('payment.providers.subtitle')}</p>
              </TableEmpty>
            ) : (
              store.items.map((p) => (
                <TableRow key={p.entity?.id} className="cursor-pointer" onClick={() => setSelected(p)}>
                  <TableCell>
                    <div className="font-medium text-foreground">{p.display_name || p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.provider_code}</div>
                  </TableCell>
                  <TableCell>{humanizeEnum(p.type, 'PAYMENT_PROVIDER_TYPE_')}</TableCell>
                  <TableCell><Badge variant={mapProviderStatusToVariant(p.status)} size="sm">{humanizeEnum(p.status, 'PAYMENT_PROVIDER_STATUS_')}</Badge></TableCell>
                  <TableCell>{p.current_success_rate ? `${p.current_success_rate}%` : '--'}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" isLoading={testingId === p.entity?.id} onClick={() => handleTest(p)}>{t('payment.providers.test')}</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setFormOpen(true); }}>{t('common.edit')}</Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)}>{t('common.remove')}</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );

  const detail = selected ? (
    <div className="p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{selected.display_name || selected.name}</h2>
        <p className="text-sm text-muted-foreground">{selected.provider_code}</p>
      </div>
      {testResult && testResult.id === selected.entity?.id && (
        <div className={`rounded-md border p-3 text-sm ${testResult.success ? 'border-success text-success' : 'border-destructive text-destructive'}`}>
          {testResult.message}
        </div>
      )}
      <dl className="divide-y divide-border text-sm">
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('common.status')}</dt><dd className="col-span-2"><Badge variant={mapProviderStatusToVariant(selected.status)} size="sm">{humanizeEnum(selected.status, 'PAYMENT_PROVIDER_STATUS_')}</Badge></dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.providers.type')}</dt><dd className="col-span-2">{humanizeEnum(selected.type, 'PAYMENT_PROVIDER_TYPE_')}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('common.description')}</dt><dd className="col-span-2">{selected.description || '--'}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.providers.supportedCurrencies')}</dt><dd className="col-span-2">{(selected.supported_currencies ?? []).join(', ') || '--'}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.providers.supportedCountries')}</dt><dd className="col-span-2">{(selected.supported_countries ?? []).join(', ') || '--'}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.providers.avgLatency')}</dt><dd className="col-span-2">{selected.current_avg_latency_ms ? `${selected.current_avg_latency_ms} ms` : '--'}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.providers.circuitBreaker')}</dt><dd className="col-span-2">{selected.circuit_breaker_open ? <Badge variant="error" size="sm">{t('payment.providers.open')}</Badge> : <Badge variant="success" size="sm">{t('payment.providers.closed')}</Badge>}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.common.created')}</dt><dd className="col-span-2">{formatDate(selected.created_at)}</dd></div>
      </dl>
      <div className="flex gap-2 pt-2">
        <Button size="sm" variant="secondary" onClick={() => { setEditing(selected); setFormOpen(true); }}>{t('common.edit')}</Button>
        <Button size="sm" variant="secondary" isLoading={testingId === selected.entity?.id} onClick={() => handleTest(selected)}>{t('payment.providers.test')}</Button>
        <Button size="sm" variant="danger" onClick={() => setDeleteTarget(selected)}>{t('common.remove')}</Button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <SplitView detail={detail} onClose={() => setSelected(null)}>
        {list}
      </SplitView>

      <ProviderFormModal
        open={formOpen}
        provider={editing}
        isSaving={store.isSaving}
        onCancel={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={submit}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('payment.providers.deleteConfirmTitle')}
        description={t('payment.providers.deleteConfirmDesc')}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
});

export default PaymentProvidersPage;
